import React, { useState, useEffect, useCallback } from 'react';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import {
    Alert,
    Autocomplete,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { zoneService } from '../../../services/zoneService';
import locationService from '../../../services/locationService';

/**
 * The contents of a destination zone.
 *
 * Under the destination-only model a zone IS its list of cities: that list is
 * what decides the price of every booking delivered to them. This replaces the
 * region-pair builder, which has no meaning once the origin plays no part.
 *
 * A city belongs to exactly one zone, so adding a city here moves it out of
 * whichever zone currently holds it; the API reports what moved.
 */
const ZoneCities = ({ zone, viewMode = false }) => {
    const { showError, showSuccess } = useApiHandler();

    const [cities, setCities] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    // Typing in the filter fetches once the user pauses, not per keystroke
    const debouncedSearch = useDebouncedValue(search);
    const [loading, setLoading] = useState(false);

    const [cityOptions, setCityOptions] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [addSearch, setAddSearch] = useState('');
    const [toAdd, setToAdd] = useState([]);
    const [saving, setSaving] = useState(false);
    const [conflicts, setConflicts] = useState([]);

    const fetchCities = useCallback(async () => {
        if (!zone?.id) return;

        setLoading(true);
        try {
            const response = await zoneService.getZoneCities(zone.id, {
                search: debouncedSearch,
                page: page + 1,
                per_page: rowsPerPage,
            });
            setCities(response.data || []);
            setTotal(response.pagination?.total || 0);
        } catch (error) {
            showError('Failed to load the cities in this zone');
        } finally {
            setLoading(false);
        }
    }, [zone?.id, debouncedSearch, page, rowsPerPage, showError]);

    useEffect(() => {
        fetchCities();
    }, [fetchCities]);

    // Options for the "add cities" picker.
    //
    // Searched server-side rather than loaded up front: /cities caps per_page at
    // 100 while there are ~600 cities, so loading "everything" would silently
    // show only the first page and hide the rest from the picker.
    useEffect(() => {
        if (viewMode) return undefined;

        let cancelled = false;
        setOptionsLoading(true);

        const timer = setTimeout(() => {
            locationService
                .getCities({ search: addSearch, per_page: 100 })
                .then((response) => {
                    if (!cancelled) setCityOptions(response.data || []);
                })
                .catch(() => {
                    if (!cancelled) showError('Failed to load the city list');
                })
                .finally(() => {
                    if (!cancelled) setOptionsLoading(false);
                });
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [viewMode, addSearch, showError]);

    const handleAdd = async (confirm = false) => {
        if (toAdd.length === 0) return;

        setSaving(true);
        try {
            // /cities returns the primary key as `id`, while the zone's own
            // city list returns it as `city_id`.
            const response = await zoneService.assignCities(
                zone.id,
                toAdd.map((c) => c.id),
                confirm
            );

            // Some of the picked cities already belong to another zone. Moving
            // them reprices every future booking to those cities, so ask first.
            if (response.requires_confirmation) {
                setConflicts(response.data?.conflicts || []);
                return;
            }

            showSuccess(response.message);
            setConflicts([]);
            setToAdd([]);
            setPage(0);
            fetchCities();
        } catch (error) {
            showError(error?.message || 'Failed to add cities');
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (city) => {
        try {
            const response = await zoneService.removeCity(zone.id, city.city_id);
            showSuccess(response.message);
            fetchCities();
        } catch (error) {
            showError(error?.message || 'Failed to remove the city');
        }
    };

    if (!zone?.id) {
        return (
            <Card>
                <CardHeader title="Cities in this Zone" />
                <CardContent>
                    <Alert severity="info">Save the zone first, then assign cities to it.</Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader
                title="Cities in this Zone"
                subheader={`Every booking delivered to one of these cities is priced as ${zone.name}.`}
                action={<Chip label={`${total} cities`} color="primary" variant="outlined" />}
            />
            <CardContent>
                {!viewMode && (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
                        <Autocomplete
                            multiple
                            sx={{ flex: 1 }}
                            options={cityOptions}
                            loading={optionsLoading}
                            value={toAdd}
                            onChange={(e, value) => setToAdd(value)}
                            onInputChange={(e, value) => setAddSearch(value)}
                            filterOptions={(x) => x}
                            getOptionLabel={(option) =>
                                `${option.name}${option.state?.name ? `, ${option.state.name}` : ''}`
                            }
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Add cities to this zone"
                                    placeholder="Type to search cities"
                                    helperText="A city belongs to one zone only. Adding it here moves it out of its current zone."
                                />
                            )}
                        />
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} /> : <AddIcon />}
                            onClick={() => handleAdd(false)}
                            disabled={saving || toAdd.length === 0}
                            sx={{ height: 56 }}
                        >
                            Add
                        </Button>
                    </Stack>
                )}

                <TextField
                    fullWidth
                    size="small"
                    label="Filter cities in this zone"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                    }}
                    sx={{ mb: 2 }}
                />

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>City</TableCell>
                                <TableCell>Code</TableCell>
                                <TableCell>State</TableCell>
                                {!viewMode && <TableCell align="right">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : cities.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">
                                            {search
                                                ? 'No cities match that filter.'
                                                : 'No cities in this zone yet, so nothing will be priced against it.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                cities.map((city) => (
                                    <TableRow key={city.city_id}>
                                        <TableCell>{city.city_name}</TableCell>
                                        <TableCell>{city.city_short || '-'}</TableCell>
                                        <TableCell>{city.state_name || '-'}</TableCell>
                                        {!viewMode && (
                                            <TableCell align="right">
                                                <Tooltip title="Remove from this zone. The city becomes unpriceable until it is given another zone.">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleRemove(city)}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    rowsPerPageOptions={[10, 25, 50]}
                    count={total}
                    page={page}
                    onPageChange={(e, value) => setPage(value)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />

            </CardContent>

            <Dialog open={conflicts.length > 0} onClose={() => setConflicts([])} maxWidth="sm" fullWidth>
                <DialogTitle>Move {conflicts.length === 1 ? 'this city' : 'these cities'} to {zone.name}?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        A city can belong to only one zone. Moving{' '}
                        {conflicts.length === 1 ? 'it' : 'them'} here removes{' '}
                        {conflicts.length === 1 ? 'it' : 'them'} from the zone shown below,
                        and future bookings to {conflicts.length === 1 ? 'that city' : 'those cities'}{' '}
                        will be priced as <strong>{zone.name}</strong>.
                    </Alert>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>City</TableCell>
                                <TableCell>Currently in</TableCell>
                                <TableCell>Will move to</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {conflicts.map((c) => (
                                <TableRow key={c.city_id}>
                                    <TableCell>{c.city_name}</TableCell>
                                    <TableCell>
                                        <Chip label={c.current_zone_name} size="small" color="default" />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={zone.name} size="small" color="primary" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConflicts([])} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => handleAdd(true)}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={18} /> : null}
                    >
                        Move to {zone.name}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default ZoneCities;
